import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  forwardRef,
  Input,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { UIRouterModule } from '@uirouter/angular';

import { AI_CREDITS_QUANTITY_STEP } from 'src/app/components/plans/plans.service';
import { PaymentScheduleOption } from './payment-schedule-picker.types';

@Component({
  selector: 'payment-schedule-picker',
  templateUrl: './payment-schedule-picker.component.html',
  styleUrls: ['./payment-schedule-picker.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PaymentSchedulePickerComponent),
      multi: true,
    },
  ],
  imports: [CommonModule, UIRouterModule],
  standalone: true,
})
export class PaymentSchedulePickerComponent
  implements ControlValueAccessor, OnChanges
{
  @Input() options: PaymentScheduleOption[] = [];
  @Input() quantity = 50;
  @Input() disabled = false;

  protected selectedProductCode: string | null = null;

  /** Last value written by the parent FormControl (source of truth). */
  private formControlValue: string | null = null;

  private onChange: (value: string | null) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.options) {
      this._reconcileSelectionFromFormValue();
    }
    this.cdr.markForCheck();
  }

  writeValue(value: string | null): void {
    this.formControlValue = value;
    this._reconcileSelectionFromFormValue();
    this.cdr.markForCheck();
  }

  /**
   * Applies formControlValue to the visual selection once options are available.
   * Preserves the FormControl value when options have not loaded yet (async init).
   */
  private _reconcileSelectionFromFormValue(): void {
    const value = this.formControlValue;

    if (value === null) {
      this.selectedProductCode = null;
      return;
    }

    const optionExists = this.options.some((o) => o.productCode === value);
    if (optionExists) {
      this.selectedProductCode = value;
      return;
    }

    if (this.options.length === 0) {
      this.selectedProductCode = value;
      return;
    }

    this.selectedProductCode = null;
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cdr.markForCheck();
  }

  protected selectOption(productCode: string): void {
    if (this.disabled || this.selectedProductCode === productCode) {
      return;
    }
    this.formControlValue = productCode;
    this.selectedProductCode = productCode;
    this.onChange(productCode);
    this.onTouched();
    this.cdr.markForCheck();
  }

  protected isSelected(option: PaymentScheduleOption): boolean {
    return this.selectedProductCode === option.productCode;
  }

  protected getMonthlyCredits(option: PaymentScheduleOption): number {
    return option.scheduleType === 'annual' ? this.quantity : 0;
  }

  protected getAnnualTotalCredits(option: PaymentScheduleOption): number {
    return this.quantity * 12;
  }

  protected getOptionDescription(option: PaymentScheduleOption): string {
    if (option.scheduleType === 'annual') {
      return `${this.quantity} monthly credits (${this.getAnnualTotalCredits(option)} total credits a year, billed annually)`;
    }

    return `Get ${this.quantity} credits to use for the next 12 months. Independent of your subscription. Charged in full today. No automatic renewal.`;
  }

  protected formatMoney(amount: number, currencyCode: string): string {
    const prefix = currencyCode ? currencyCode.toUpperCase() + ' ' : '';
    return `${prefix}$${amount.toFixed(2)}`;
  }

  protected getAmountPerMonth(option: PaymentScheduleOption): number {
    if (option.scheduleType !== 'annual') {
      return 0;
    }
    return option.monthlyPrice * (this.quantity / AI_CREDITS_QUANTITY_STEP);
  }

  protected getAnnualSavings(option: PaymentScheduleOption): number {
    if (option.scheduleType !== 'annual') {
      return 0;
    }
    return option.annualSavings * (this.quantity / AI_CREDITS_QUANTITY_STEP);
  }
}
